/**
 * ObjectLayer — warstwa sceny: spawn, z-sort, pickup/hold/drop, give, snap, kolejka intencji.
 * PLAN-V2 §3.1 / §3.3. Cel psa przez InteractTarget (luźny typ — bez importu avatar/).
 */

import Phaser from 'phaser';
import {
  InteractiveObject,
  narrateObjectDef,
  resolveObjectNarration,
} from './InteractiveObject.js';
import {
  completeCurrent,
  createIntentionQueue,
  dist2,
  enqueueIntention,
  isInRange,
  clearQueue,
  type Intention,
  type IntentionQueueState,
} from './intentionQueue.js';
import { findSnapTarget } from './snap.js';
import {
  projectGroundDrop,
  type GroundDropBounds,
} from './dropPolicy.js';
import { playFx, fxForReaction } from './fx.js';
import {
  DEFAULT_INTERACT_RANGE,
  type InteractEvent,
  type InteractListener,
  type InteractTarget,
  type InteractionKind,
  type ObjectDef,
  type SnapZone,
  type SpawnPlacement,
} from './types.js';

/** Re-export G15 — scenerie / testy mogą czytać etykietę lektora bez duplikacji logiki. */
export { narrateObjectDef, resolveObjectNarration };

export type ObjectLayerOpts = {
  scene: Phaser.Scene;
  defs: ObjectDef[];
  /** Cel gracza (pies) — moveTo / holdItem / playAction. */
  target?: InteractTarget | null;
  /** Zasięg interakcji bez podejścia. */
  interactRange?: number;
  /** Give: dodatkowe cele (NPC), poza target. */
  giveTargets?: InteractTarget[];
  /** Pas podłogi dla zwykłych dropów; snap/give zachowują pozycje autorskie. */
  groundBounds?: GroundDropBounds;
  /** Linia ziemi: drop powyżej (y < floorY) ląduje na floorY, nie na minY. */
  floorY?: number;
};

export class ObjectLayer {
  private readonly scene: Phaser.Scene;
  private readonly defs = new Map<string, ObjectDef>();
  private readonly objects = new Map<string, InteractiveObject>();
  private snapZones: SnapZone[] = [];
  private target: InteractTarget | null;
  private giveTargets: InteractTarget[];
  private readonly interactRange: number;
  private readonly groundBounds: GroundDropBounds | null;
  private readonly floorY: number | undefined;
  private queue: IntentionQueueState = createIntentionQueue();
  private listeners = new Set<InteractListener>();
  private instanceSeq = 0;
  private held: InteractiveObject | null = null;
  private holdFollowPointer = false;
  private unsubs: Array<() => void> = [];
  private approachActive: Intention | null = null;
  /** true gdy executeAction z intencji jest w toku (blokada podwójnego odpalenia). */
  private intentionBusy = false;

  constructor(opts: ObjectLayerOpts) {
    this.scene = opts.scene;
    this.target = opts.target ?? null;
    this.giveTargets = opts.giveTargets ?? [];
    this.interactRange = opts.interactRange ?? DEFAULT_INTERACT_RANGE;
    this.groundBounds = opts.groundBounds ?? null;
    this.floorY = opts.floorY;
    for (const d of opts.defs) {
      this.defs.set(d.id, d);
    }
  }

  setTarget(t: InteractTarget | null): void {
    this.target = t;
  }

  setGiveTargets(list: InteractTarget[]): void {
    this.giveTargets = list;
  }

  setSnapZones(zones: SnapZone[]): void {
    this.snapZones = zones.slice();
  }

  addSnapZone(zone: SnapZone): void {
    this.snapZones.push(zone);
  }

  on(listener: InteractListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(evt: InteractEvent): void {
    for (const l of this.listeners) l(evt);
  }

  getObjects(): InteractiveObject[] {
    return [...this.objects.values()];
  }

  getObject(instanceId: string): InteractiveObject | undefined {
    return this.objects.get(instanceId);
  }

  get intentionQueue(): IntentionQueueState {
    return this.queue;
  }

  get heldObject(): InteractiveObject | null {
    return this.held;
  }

  /**
   * Spawn z listy {defId,x,y}. Pomija nieznane defId (log w dev).
   */
  spawn(placements: SpawnPlacement[]): InteractiveObject[] {
    const created: InteractiveObject[] = [];
    for (const p of placements) {
      const def = this.defs.get(p.defId);
      if (!def) {
        if (import.meta.env?.DEV) {
          // eslint-disable-next-line no-console
          console.warn(`[ObjectLayer] brak def: ${p.defId}`);
        }
        continue;
      }
      this.instanceSeq += 1;
      const instanceId = `${def.id}#${this.instanceSeq}`;
      const obj = new InteractiveObject({
        scene: this.scene,
        def,
        x: p.x,
        y: p.y,
        instanceId,
      });
      const unsub = obj.on((evt) => this.onObjectEvent(obj, evt));
      this.unsubs.push(unsub);
      this.objects.set(instanceId, obj);
      created.push(obj);
    }
    this.zSort();
    return created;
  }

  /** Z-sort po y (niżej na ekranie = wyższy depth). */
  zSort(): void {
    for (const obj of this.objects.values()) {
      if (!obj.isDragging && !obj.isHeld) {
        obj.refreshDepth();
      }
    }
  }

  private targetPos(): { x: number; y: number } | null {
    if (!this.target) return null;
    if (this.target.getPosition) return this.target.getPosition();
    if (typeof this.target.x === 'number' && typeof this.target.y === 'number') {
      return { x: this.target.x, y: this.target.y };
    }
    return null;
  }

  private onObjectEvent(obj: InteractiveObject, evt: InteractEvent): void {
    this.emit(evt);

    if (evt.type === 'tap') {
      this.handleTap(obj, 'tap');
      return;
    }
    if (evt.type === 'drag-start') {
      if (obj.hasKind('pickup')) {
        // pickup start — trzymamy za wskaźnikiem
        this.held = obj;
        obj.markHeld(true);
        this.holdFollowPointer = true;
      }
      return;
    }
    if (evt.type === 'drag-end') {
      this.handleDrop(obj, evt.x, evt.y);
      return;
    }
    if (evt.type === 'bounce') {
      // already handled in object
      return;
    }
  }

  /**
   * Tap / intencja: w zasięgu → akcja; poza → moveTo + kolejka.
   */
  handleTap(obj: InteractiveObject, kind: InteractionKind): void {
    const preferred = this.pickActionKind(obj, kind);
    const pos = this.targetPos();
    const inRange =
      !this.target ||
      !pos ||
      dist2(pos.x, pos.y, obj.x, obj.y) <= this.interactRange;

    if (inRange) {
      void this.executeAction(obj, preferred);
      return;
    }

    const result = enqueueIntention(this.queue, {
      objectId: obj.instanceId,
      defId: obj.def.id,
      kind: preferred,
      approachX: obj.x,
      approachY: obj.y,
      range: this.interactRange,
    });
    this.queue = result.queue;
    this.emit({
      type: 'intention-enqueued',
      objectId: obj.instanceId,
      defId: obj.def.id,
      x: obj.x,
      y: obj.y,
      kind: preferred,
      payload: { intentionId: result.intention.id, started: result.started },
    });

    if (result.started) {
      this.startApproach(result.intention);
    }
  }

  private pickActionKind(obj: InteractiveObject, fallback: InteractionKind): InteractionKind {
    const order: InteractionKind[] = [
      'consume',
      'give',
      'toggle',
      'pickup',
      'bounce',
      'drag',
      'tap',
    ];
    for (const k of order) {
      if (obj.hasKind(k)) return k;
    }
    return fallback;
  }

  private startApproach(intention: Intention): void {
    this.approachActive = intention;
    this.emit({
      type: 'intention-approach',
      objectId: intention.objectId,
      defId: intention.defId,
      x: intention.approachX,
      y: intention.approachY,
      kind: intention.kind,
    });
    this.target?.moveTo(intention.approachX, intention.approachY);
  }

  private async executeAction(
    obj: InteractiveObject,
    kind: InteractionKind,
  ): Promise<void> {
    switch (kind) {
      case 'toggle':
        obj.doToggle();
        await this.playDogReaction(obj);
        break;
      case 'consume':
        obj.doConsume();
        await this.playDogReaction(obj);
        break;
      case 'pickup':
        this.pickupToMouth(obj);
        break;
      case 'give':
        await this.tryGive(obj);
        break;
      case 'bounce':
        // kick handled by object input; if intentional, noop
        break;
      case 'tap':
      default:
        if (obj.def.fx) {
          playFx(this.scene, obj.def.fx, obj.x, obj.y);
        } else {
          playFx(this.scene, 'sparkle', obj.x, obj.y);
        }
        await this.playDogReaction(obj);
        break;
    }
  }

  private async playDogReaction(obj: InteractiveObject): Promise<void> {
    const r = obj.def.dogReaction;
    if (!r || !this.target) return;
    const action = r === 'sniff' ? 'happy' : r;
    try {
      await this.target.playAction(action);
    } catch {
      /* ignore */
    }
    if (!obj.def.fx) {
      playFx(this.scene, fxForReaction(r), obj.x, obj.y);
    }
  }

  /** Przedmiot do pyszczka psa (holdItem). */
  private pickupToMouth(obj: InteractiveObject): void {
    this.held = obj;
    obj.markHeld(true);
    this.holdFollowPointer = false;
    const tex = obj.def.texture;
    this.target?.holdItem(tex);
    // schowaj na ziemi — „w pyszczku"
    obj.container.setVisible(false);
  }

  private handleDrop(obj: InteractiveObject, x: number, y: number): void {
    this.holdFollowPointer = false;

    // snap?
    const snap = findSnapTarget(x, y, this.snapZones, obj.def.snapTargets ?? null);
    if (snap) {
      obj.setPosition(snap.zone.x, snap.zone.y);
      obj.playDropBounce();
      this.emit({
        type: 'snap',
        objectId: obj.instanceId,
        defId: obj.def.id,
        x: snap.zone.x,
        y: snap.zone.y,
        snapZoneId: snap.zone.id,
        kind: 'drag',
      });
      if (obj.def.fx) playFx(this.scene, obj.def.fx, snap.zone.x, snap.zone.y);
      else playFx(this.scene, 'sparkle', snap.zone.x, snap.zone.y);
      this.clearHeld(obj);
      return;
    }

    // give na NPC/psa?
    if (obj.hasKind('give') || obj.hasKind('consume')) {
      const giveHit = this.findGiveHit(x, y);
      if (giveHit) {
        void this.completeGive(obj, giveHit);
        return;
      }
    }

    // zwykły drop na ziemię (snap/give już wróciły wyżej)
    const ground = this.groundBounds
      ? projectGroundDrop({ x, y }, this.groundBounds, this.floorY)
      : { x, y };
    obj.setPosition(ground.x, ground.y);
    // WAVE7 / Sol P2: wyczyść pyszczek PRZED clearHeld (held===obj inaczej nigdy)
    if (this.held === obj) {
      this.target?.holdItem(null);
    }
    this.clearHeld(obj);
  }

  private clearHeld(obj: InteractiveObject): void {
    if (this.held === obj) {
      if (obj.isHeld) obj.markHeld(false);
      obj.container.setVisible(true);
      this.held = null;
    }
  }

  private findGiveHit(x: number, y: number): InteractTarget | null {
    const candidates: InteractTarget[] = [];
    if (this.target) candidates.push(this.target);
    candidates.push(...this.giveTargets);
    const range = this.interactRange;
    for (const t of candidates) {
      const p = t.getPosition
        ? t.getPosition()
        : typeof t.x === 'number' && typeof t.y === 'number'
          ? { x: t.x, y: t.y }
          : null;
      if (!p) continue;
      if (dist2(x, y, p.x, p.y) <= range) return t;
    }
    return null;
  }

  private async tryGive(obj: InteractiveObject): Promise<void> {
    const hit = this.findGiveHit(obj.x, obj.y) ?? this.target;
    if (!hit) return;
    await this.completeGive(obj, hit);
  }

  private async completeGive(obj: InteractiveObject, who: InteractTarget): Promise<void> {
    playFx(this.scene, obj.def.fx ?? 'hearts', obj.x, obj.y);
    this.emit({
      type: 'give',
      objectId: obj.instanceId,
      defId: obj.def.id,
      x: obj.x,
      y: obj.y,
      kind: 'give',
    });
    if (obj.hasKind('consume') || obj.def.dogReaction === 'eat') {
      obj.doConsume();
    }
    try {
      await who.playAction(obj.def.dogReaction === 'eat' ? 'eat' : 'happy');
    } catch {
      /* ignore */
    }
    this.target?.holdItem(null);
    this.clearHeld(obj);
    // po give chowamy / zostawiamy empty
    if (obj.state.consumed) {
      obj.container.setAlpha(0.4);
    }
  }

  /**
   * Pętla warstwy: intencje (podejście), hold za wskaźnikiem, update obiektów, z-sort.
   */
  update(dtSec: number): void {
    // hold za wskaźnikiem
    if (this.held && this.holdFollowPointer) {
      const p = this.scene.input.activePointer;
      this.held.setPosition(p.worldX, p.worldY - 20);
    }

    // kolejka intencji — sprawdź zasięg
    const cur = this.queue.current;
    if (cur && this.approachActive?.id === cur.id && !this.intentionBusy) {
      const pos = this.targetPos();
      if (pos && isInRange(cur, pos.x, pos.y)) {
        this.intentionBusy = true;
        const obj = this.objects.get(cur.objectId);
        if (obj) {
          void this.executeAction(obj, cur.kind).finally(() => {
            this.emit({
              type: 'intention-done',
              objectId: cur.objectId,
              defId: cur.defId,
              x: obj.x,
              y: obj.y,
              kind: cur.kind,
            });
            const advanced = completeCurrent(this.queue);
            this.queue = advanced.queue;
            this.approachActive = null;
            this.intentionBusy = false;
            if (advanced.next) {
              this.startApproach(advanced.next);
            }
          });
        } else {
          const advanced = completeCurrent(this.queue);
          this.queue = advanced.queue;
          this.approachActive = null;
          this.intentionBusy = false;
          if (advanced.next) this.startApproach(advanced.next);
        }
      } else if (pos && this.target && this.target.isMoving === false) {
        // utknął — ponów moveTo
        this.target.moveTo(cur.approachX, cur.approachY);
      }
    }

    for (const obj of this.objects.values()) {
      obj.update(dtSec);
    }
    this.zSort();
  }

  /** Wyczyść kolejkę i obiekty (wyjście ze sceny). */
  destroy(): void {
    this.queue = clearQueue(this.queue);
    for (const u of this.unsubs) u();
    this.unsubs = [];
    for (const obj of this.objects.values()) obj.destroy();
    this.objects.clear();
    this.held = null;
    this.listeners.clear();
  }
}
